import { Fragment, type ReactNode } from 'react';

// Split on http(s) URLs, keeping the URLs as capture groups so they land at the
// odd indices of the resulting array. Bare customer-page paths count too: the
// server's texts carry `/customer-deposit/…` or `/customer-waiver/…` without an
// origin until a public app origin is approved (B6, customerPageLink).
const URL_RE = /(https?:\/\/[^\s]+|\/customer-(?:deposit|waiver)\/[^\s]+)/g;
// Punctuation that commonly trails a URL in prose but isn't part of the address
// (a sentence-ending period, a comma, a closing bracket). Peeled off the href
// and rendered as ordinary text.
const TRAILING_PUNCTUATION = /[.,;:!?)\]}]+$/;

interface Props {
  text: string;
  /** Class for each rendered <a>. Defaults to the message-link treatment. */
  linkClassName?: string;
}

/**
 * Renders message text with any http(s) URLs turned into clickable links — e.g.
 * the deposit-pay link the studio sends a customer inside a DM. Links open in a
 * new tab because they point at the customer-facing page (the same URL the
 * customer would tap on their own phone). Everything else passes through as
 * plain text unchanged.
 */
export default function Linkify({ text, linkClassName }: Props): ReactNode {
  const cls =
    linkClassName ??
    'text-azure underline underline-offset-2 decoration-azure/50 hover:text-gold-bright break-all';
  return text.split(URL_RE).map((segment, i) => {
    // Even indices are the plain-text runs between matches; odd indices are URLs.
    if (i % 2 === 0) return <Fragment key={i}>{segment}</Fragment>;
    const trailing = segment.match(TRAILING_PUNCTUATION)?.[0] ?? '';
    const href = trailing ? segment.slice(0, -trailing.length) : segment;
    return (
      <Fragment key={i}>
        <a
          href={href}
          target="_blank"
          rel="noreferrer noopener"
          className={cls}
          data-testid="message-link"
        >
          {href}
        </a>
        {trailing}
      </Fragment>
    );
  });
}
